import React, { useCallback, useEffect, useState } from "react";
import { Mail, Phone, Loader2, UserCircle2 } from "lucide-react";
import { Card, CardHeader, CardContent } from "components/ui/card";
import { useAuth } from "contexts/AuthContext";
import { useI18n } from "i18n";
import api from "lib/api";
import ContactRow from "components/ContactRow";

// "My contacts": the signed-in user proves ownership of their own e-mail / phone.
// The value is only written to the account after the code sent to it is confirmed, so a typo
// (or an address someone does not own) never lands on the account.
export default function Profile() {
  const { t } = useI18n();
  const { user, fetchUser } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/api/me/contacts/");
      setData(r.data);
    } catch { setData(null); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const done = () => { load(); fetchUser && fetchUser(); };

  if (loading) {
    return <div className="flex justify-center py-16">
      <Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }

  return (
    <div className="animate-fade-in w-full">
      <div className="flex items-center gap-2 mb-1">
        <UserCircle2 className="h-6 w-6 text-primary" />
        <h1 className="text-2xl font-bold tracking-tight">{t("profile.title", "Meus dados")}</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-5">
        {t("profile.subtitle", "Confirme a posse do seu e-mail e telefone. Toda alteração exige um código enviado para o próprio contato.")}
      </p>

      <Card className="mb-4">
        <CardHeader className="flex-row items-center gap-2 pb-2">
          <h2 className="text-sm font-semibold tracking-tight">
            {user?.first_name ? `${user.first_name} ${user.last_name || ""}`.trim() : user?.email}
          </h2>
        </CardHeader>
        <CardContent className="pt-0">
          <ContactRow kind="email" icon={Mail} t={t} client={api} base="/api/me/contacts"
            label={t("profile.email", "E-mail")}
            value={data?.email} masked={data?.email} verified={data?.email_verified}
            onDone={done} />
          <ContactRow kind="phone" icon={Phone} t={t} client={api} base="/api/me/contacts"
            label={t("profile.phone", "Telefone (WhatsApp)")}
            value={data?.phone} masked={data?.phone} verified={data?.phone_verified}
            onDone={done} />
        </CardContent>
      </Card>
    </div>
  );
}
